import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Input,
  input,
  OnInit,
  Output,
  signal,
  ChangeDetectionStrategy
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router, RouterModule } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { MatDialog } from "@angular/material/dialog";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { first, switchMap, combineLatest } from "rxjs";
import { LorenAssetGalleryPhoto } from "../../../features/loren-assets/interfaces/loren-asset-gallery-photo.interface";
import { LorenAssetService } from "../../../features/loren-assets/services/loren-asset-service/loren-asset.service";
import { LorenImageModule } from "../../pipes/loren-image/loren-image.module";
import { LorenMetricComponent } from "./loren-metric/loren-metric.component";
import { LorenBaseFeature } from "../../base-components/loren-base-feature/loren-base-feature";
import {
  LorenBreakpointObserverService
} from "../../services/loren-breakpoint-observer-service/loren-breakpoint-observer.service";
import { LorenNavService } from "../loren-nav/components/loren-nav/services/loren-nav-service/loren-nav.service";
import { LorenFeature } from "../../enums/loren-feature.enum";
import { LorenFeatureToggleService } from "../../services/loren-feature-toggle-service/loren-feature-toggle.service";
import { LOREN_ROUTES } from "../../constants/loren-routes.constants";
import { LorenDateModule } from "../../pipes/loren-date/loren-date.module";
import {
  LorenAssetDetailsPhotosDialogComponent
} from "../../../features/loren-asset-details/components/loren-asset-details-photos-dialog/loren-asset-details-photos-dialog.component";
import {
  LorenAssetDetailsUploadPhotoDialogComponent
} from "../../../features/loren-asset-details/components/loren-asset-details-upload-photo-dialog/loren-asset-details-upload-photo-dialog.component";
import { LorenSnackBarService } from "../../services/loren-snack-bar-service/loren-snack-bar.service";
import { LorenMeteringType } from "../../../features/loren-assets/interfaces/loren-metering-type";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { AssetService } from "../../../features/asset/asset.service";
import { map } from "rxjs/operators";
import { LorenDashboardCardUtilsService } from "../loren-dashboard-card/services/loren-dashboard-card-utils.service";
import { DeviceService } from "../../../features/device/device.service";
import { MeticConfig } from "../../../features/metric/metric";

@Component({
  selector: "loren-asset-card",
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatCardModule,
    MatIcon,
    MatTooltip,
    MatProgressSpinner,
    TranslocoPipe,
    LorenImageModule,
    LorenMetricComponent,
    LorenDateModule,
  ],
  templateUrl: "./loren-asset-card.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./loren-asset-card.component.scss",
})
export class LorenAssetCardComponent extends LorenBaseFeature implements OnInit {
  private matDialog = inject(MatDialog);
  private translate = inject(TranslocoService);
  private featureToggleService = inject(LorenFeatureToggleService);
  protected breakpointObserverService: LorenBreakpointObserverService;
  protected navService: LorenNavService;
  private readonly assetService = inject(AssetService);
  private legacyAssetService = inject(LorenAssetService);
  private snackbarService = inject(LorenSnackBarService);
  private router = inject(Router);
  private readonly deviceService = inject(DeviceService);
  MeticConfig = MeticConfig;
  assetId = input.required<number>();
  private readonly assetId$ = toObservable(this.assetId);

  private readonly cardModel$ = this.assetId$.pipe(
    takeUntilDestroyed(this.destroyRef),
    switchMap(id => combineLatest([this.assetService.get(id), this.deviceService.all])),
    map(([asset, devices]) => LorenDashboardCardUtilsService.transformAssetToDashboardCardModel(
      asset,
      devices,
    )),
  )

  asset = toSignal(this.cardModel$)
  @Input() canRemove: boolean;
  @Input() rowView: boolean;
  @Input() tableView: boolean;
  @Input() first: boolean;
  @Input() last: boolean;
  @Output() remove = new EventEmitter<number>();

  assetPhotos = signal<LorenAssetGalleryPhoto[]>([]);
  currentMetric = signal<number>(0);
  hasMultipleMetrics = computed(() => this.asset().meteringViews.length > 1);
  numberOfMetricsClassname = signal(null);
  pagesNumber = signal(null);
  showingMetrics = signal([]);
  showPhotos = signal<boolean>(false);
  photosLoading = signal<boolean>(false);

  generalAssetError = signal<boolean>(false);
  photosError = signal<boolean>(false);
  url = computed(() => `/${LOREN_ROUTES.ASSET}/${this.asset().assetId}`);
  hasGpsTracking = computed(() => {
    if (this.tableView) {
      return this.asset().meteringViews.some(view => view.type === LorenMeteringType.GPS_TRACKER_SENSOR);
    }
    const currentMetrics = this.showingMetrics();
    return currentMetrics.some(metric => metric.type === LorenMeteringType.GPS_TRACKER_SENSOR);
  });

  isDashboardRoute = computed(() => {
    const currentUrl = this.router.url;
    return currentUrl === '/dashboard' || currentUrl === '/' || currentUrl === '';
  });

  private numberOfMetrics = signal(null);

  constructor() {
    const breakpointObserverService = inject(LorenBreakpointObserverService);
    const navService = inject(LorenNavService);

    super();

    this.breakpointObserverService = breakpointObserverService;
    this.navService = navService;



    effect(() => {
      // Workaround so we don't have to refactor everything
      // This triggers rerender so loren-metric component is properly shown
      if (this.asset()) {
        this.getNumberOfMetrics();
      }
    });
  }

  ngOnInit() {
    this.featureToggleService
        .userHasFeature(LorenFeature.IMAGE_GALLERY)
        .pipe(takeUntilDestroyed(this.destroyRef), first())
        .subscribe((data) => {
          this.showPhotos.set(data);
          if (data) this.getPhotos();
        });
  }

  getMetering(id: number) {
    return this.asset().meteringRaw.find(x => x.id === id);
  }

  changeMetric(newMetric: number) {
    this.currentMetric.set(newMetric);
    this.getShowingMetrics();
  }

  handleLocationPinClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.router.navigateByUrl(LOREN_ROUTES.MAP, {
      state: { assetId: this.asset().assetId },
    });
  }

  getNumberOfMetrics() {
    if (this.isHandset) {
      this.rowView = false;
    }
    if (this.isTablet) {
      this.numberOfMetrics.set(2);
    }
    if (this.isMedium || this.isLarge) {
      this.numberOfMetrics.set(3);
    }
    if (this.isExtraLarge) {
      this.numberOfMetrics.set(4);
    }

    if (this.isCustomXXL) {
      this.numberOfMetrics.set(5);
    }
    this.pagesNumber.set(
      Math.ceil(this.asset().meteringViews.length / this.numberOfMetrics())
    );
    this.numberOfMetricsClassname.set(
      Math.min(this.asset().meteringViews.length, this.numberOfMetrics())
    );
    this.getShowingMetrics();
  }

  openPhotosDialog() {
    if (!this.assetPhotos()?.length) return;

    const dialogRef = this.matDialog.open(
      LorenAssetDetailsPhotosDialogComponent,
      {
        width: this.isHandset ? "80%" : "50%",
        height: "95%",
        data: {
          assetName: this.asset().assetName,
          allPhotos: this.assetPhotos(),
          currentPhoto: 0,
        },
      }
    );
    return dialogRef.afterClosed();
  }

  openUploadPhotoDialog() {
    const dialogRef = this.matDialog.open(
      LorenAssetDetailsUploadPhotoDialogComponent,
      {
        width: this.isHandset ? "80%" : "40%",
        height: "80%",
        data: {
          assetName: this.asset().assetName,
        },
      }
    );
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) return;
      const assetPhotoObj = {
        assetCondition: res.assetCondition,
        comment: res.comment,
        photo: res.photo.file,
      };
      this.legacyAssetService
        .addAssetPhoto(this.asset().assetId, assetPhotoObj)
        .subscribe({
          next: () => this.getPhotos(),
          error: () =>
            this.snackbarService.showErrorMessage(
              this.translate.translate("error_page.general_error_message")
            ),
        });
    });
  }

  private getShowingMetrics() {
    if (!this.rowView) {
      this.showingMetrics.set([this.asset().meteringViews[this.currentMetric()]]);
    }

    if (this.rowView) {
      if (this.asset().meteringViews.length > this.numberOfMetrics()) {
        this.showingMetrics.set(
          this.asset().meteringViews.slice(
            this.currentMetric() * this.numberOfMetrics(),
            this.currentMetric() * this.numberOfMetrics() +
            this.numberOfMetrics()
          )
        );
      }
      if (this.asset().meteringViews.length <= this.numberOfMetrics()) {
        this.showingMetrics.set(this.asset().meteringViews);
      }
    }
  }

  private getPhotos() {
    this.photosLoading.set(true);
    this.assetId$.pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(assetID => this.legacyAssetService.getAssetPhotos(assetID)),
    ).subscribe({
      next: (res: {
        assetId: number;
        photos: LorenAssetGalleryPhoto[];
      }) => {
        this.photosError.set(false);
        this.assetPhotos.set(res.photos);
        this.photosLoading.set(false);
      },
      error: () => this.photosError.set(true),
    });
  }
}
